// Made-up data for the walkthroughs.
//
// Drew, 2026-10-01: the tour runs on mock data from start to finish — nobody
// types, nothing is saved, every page has something to point at. The wizard
// opens on a made-up client already filled in (SAMPLE_WIZARD_CLIENT, with an
// idea already on the strategy page), My Clients shows three made-up clients
// with real calculations, Downline Management shows a made-up downline. All of
// it goes away when the walkthrough ends, and the person's own data is back.
//
// Nothing here is saved. Sample clients live in Index's in-memory demo store
// (every button works on them, nothing reaches the database); the sample
// downline is a payload handed to the tree builder in place of the RPC's, with
// every action on it refused by a toast.

import type { TaxScenario, TaxScenarioInput, TaxResults } from '@/tax/types';
import { DEFAULT_INPUT } from '@/tax/types';
import type { HierarchyPayload } from '@/components/org/hierarchyLive';
import { makeDefaultVariant, type StrategyVariant } from '@/components/wizard/steps/StrategyBuilderStep';

// ─── Sample clients ────────────────────────────────────────────────────────

/** One made-up client: a starting point and the ideas tried on it. */
interface SampleClient {
  base: TaxScenarioInput;
  /**
   * Strategy suffixes and the fields they move, in the order they were "added".
   * `altAtMax` sizes the alt to the engine's own ceiling for that strategy's
   * income (`hpeMaxInvestment`, the 50%-of-AGI pool over the deduction
   * multiplier), the way the card's Auto-max does — never a typed amount, which
   * is how the first draft shipped an alt at twice the ceiling (Drew,
   * 2026-10-06: "why would you put it over the 50%?").
   */
  strategies: { suffix: string; changes: Partial<TaxScenarioInput>; altAtMax?: boolean }[];
}

/**
 * The client the wizard opens on during the tour. Drew, 2026-10-01: "make it
 * intricate … pension and Schedule C and everything so they can see the whole
 * thing working together … rentals, because that's the kind of shit we're
 * dealing with, high net worth clients. The alt's not going to be something
 * small. Harvest a million in long-term gains so you can see the Roth cover it."
 *
 * A married couple in California, both over 65 (Drew, 2026-10-05: "so we can
 * show the IRMAA changes as well" — the Medicare premium surcharge moves with
 * income, so the gain harvest and the Roth conversion each push the premium up
 * a tier and the comparison shows it): his W-2 and a consulting business on the
 * side (Schedule C, a specified service, so the business income deduction
 * phases out), her pension, both drawing Social Security, interest and
 * dividends with tax-free interest in the mix, a rental they run themselves, a
 * K-1 from a partnership, a big mortgage, property tax well over the cap, cash
 * gifts, the self-employed adjustments, and a modest long-term gain in the
 * baseline. Every page of the wizard has something filled in.
 */
export const SAMPLE_WIZARD_CLIENT: TaxScenarioInput = {
  ...DEFAULT_INPUT,
  name: 'Johnson Family',
  filingStatus: 'MFJ',
  state: 'California',
  taxYear: 2026,
  tp65Plus: true,
  sp65Plus: true,
  tp55Plus: true,
  sp55Plus: true,
  // Income
  wages: 240000,
  w2SocialSecurityWages: 240000,
  nonGovtPensionSP: 42000,
  grossSocialSecurity: 58000,
  interestIncome: 18000,
  taxExemptInterest: 9000,
  ordinaryDividends: 46000,
  qualifiedDividends: 38000,
  longTermCapitalGains: 40000,
  // Business (Schedule C, consulting — a specified service business)
  schedCGrossReceipts: 380000,
  schedCTotalExpenses: 140000,
  qbiSSTB: true,
  // Rental and K-1
  rentalActive: true,
  rentalIncome: 72000,
  rentalTotalExpenses: 51000,
  k1OrdinaryBusinessIncome: 85000,
  k1RentalRealEstateIncome: 12000,
  // Deductions
  saltStateLocal: 38000,
  saltRealEstate: 22000,
  saltPersonalProperty: 1800,
  homeMortgageInterest: 31000,
  cashGiftsPublicCharity: 25000,
  // Adjustments
  selfEmployedHealthInsurance: 18000,
  selfEmployedRetirement: 40000,
  hsaDeduction: 8000,
  // Tax already paid
  federalWithholding: 52000,
  estimatedTaxPayments: 60000,
  stateWithholding: 21000,
  applyACA: true,
  applyNIIT: true,
  rothGrowthYears: 8,
};

/** The strategy the sample's Strategies page opens with: harvest the gain, convert, and put the alt on top at its max. */
const SAMPLE_STRATEGY = { longTermCapitalGains: 1000000, rothConversionAmount: 400000 };

/**
 * The strategy card already on the page when the wizard opens during the tour:
 * sell a million of long-term gains, convert four hundred thousand to Roth, and
 * let Auto-max size the alt to the room all that income makes (the card snaps
 * it to the engine's ceiling on its first calculation). The same strategy the
 * sample client carries on My Clients, so the comparison the tour ends on is
 * the one the folder holds.
 */
export function sampleWizardVariants(): StrategyVariant[] {
  const v = makeDefaultVariant();
  return [{
    ...v,
    longTermCapitalGains: SAMPLE_STRATEGY.longTermCapitalGains,
    roth: { ...v.roth, enabled: true, conversionAmount: SAMPLE_STRATEGY.rothConversionAmount },
    hpe: { ...v.hpe, enabled: true, amount: 0 },
    autoMaxAlt: true,
  }];
}

const SAMPLE_CLIENTS: SampleClient[] = [
  {
    base: SAMPLE_WIZARD_CLIENT,
    strategies: [
      { suffix: 'LTCG', changes: { longTermCapitalGains: SAMPLE_STRATEGY.longTermCapitalGains } },
      { suffix: 'Roth + Alt + LTCG', changes: SAMPLE_STRATEGY, altAtMax: true },
    ],
  },
  {
    base: {
      ...DEFAULT_INPUT,
      name: 'Maria Alvarez',
      filingStatus: 'S',
      state: 'Texas',
      taxYear: 2026,
      wages: 0,
      schedCGrossReceipts: 420000,
      schedCTotalExpenses: 165000,
      interestIncome: 4100,
      ordinaryDividends: 12000,
      qualifiedDividends: 9500,
      longTermCapitalGains: 18000,
      saltStateLocal: 0,
      saltRealEstate: 11000,
      homeMortgageInterest: 9800,
      cashGiftsPublicCharity: 4000,
    },
    strategies: [
      { suffix: 'LTCG', changes: { longTermCapitalGains: 90000 } },
      { suffix: 'Alt + LTCG', changes: { longTermCapitalGains: 90000 }, altAtMax: true },
    ],
  },
  {
    base: {
      ...DEFAULT_INPUT,
      name: 'Tom & Linda Nguyen',
      filingStatus: 'MFJ',
      state: 'Florida',
      taxYear: 2026,
      tp65Plus: true,
      sp65Plus: true,
      tp55Plus: true,
      sp55Plus: true,
      wages: 0,
      grossSocialSecurity: 52000,
      iraDistributionConversion: 68000,
      nonGovtPensionTP: 24000,
      interestIncome: 6200,
      ordinaryDividends: 15000,
      qualifiedDividends: 13000,
      longTermCapitalGains: 9000,
      cashGiftsPublicCharity: 8000,
    },
    strategies: [
      { suffix: 'Roth', changes: { rothConversionAmount: 50000 } },
    ],
  },
];

export type SampleCalc = (input: TaxScenarioInput) => Promise<TaxResults>;

/**
 * Build the sample clients as scenarios the app can show: every one calculated
 * for real through `calc` (the same server call the wizard uses), grouped the
 * way saved clients are — a baseline first, then each idea named
 * "Client (Idea)" — with ids that can never collide with a saved row.
 */
export async function buildSampleClients(calc: SampleCalc, userId?: string): Promise<TaxScenario[]> {
  const now = Date.now();
  const stamp = (minutesAgo: number) => new Date(now - minutesAgo * 60_000).toISOString();
  const out: TaxScenario[] = [];
  await Promise.all(
    SAMPLE_CLIENTS.map(async (client, ci) => {
      const clientId = `sample-client-${ci + 1}`;
      const baseline: TaxScenarioInput = { ...client.base, alternativeInvestmentAmount: 0 };
      const inputs: TaxScenarioInput[] = await Promise.all([
        Promise.resolve(baseline),
        ...client.strategies.map(async s => {
          const input: TaxScenarioInput = { ...baseline, ...s.changes, name: `${client.base.name} (${s.suffix})` };
          if (!s.altAtMax) return input;
          // The alt does not feed back into its own ceiling, so one run with it
          // at zero reads the max for this strategy's income.
          const probe = await calc({ ...input, alternativeInvestmentAmount: 0 });
          return { ...input, alternativeInvestmentAmount: probe.hpeMaxInvestment };
        }),
      ]);
      const results = await Promise.all(inputs.map(calc));
      inputs.forEach((input, i) => {
        out.push({
          id: `${clientId}-${i}`,
          clientId,
          rootClientId: clientId,
          input,
          results: results[i],
          // Newest client first on the grid, ideas after their baseline.
          createdAt: stamp((SAMPLE_CLIENTS.length - ci) * 60 * 24 + (inputs.length - i)),
          updatedAt: stamp((SAMPLE_CLIENTS.length - ci) * 60 * 24 - i * 30),
          sortOrder: i,
          userId,
        });
      });
    }),
  );
  return out;
}

/** True when a scenario is one of the made-up ones. */
export function isSampleScenario(s: Pick<TaxScenario, 'clientId'>): boolean {
  return s.clientId.startsWith('sample-client-');
}

// ─── Sample downline ───────────────────────────────────────────────────────

const daysAgo = (d: number) => new Date(Date.now() - d * 86_400_000).toISOString();

/**
 * A made-up IMO with two agencies, a team, a handful of agents, an admin, one
 * suspended person, clients on the agents, alt investments switched off for one
 * agency (so the Strategies column shows what "off here" looks like) and a few
 * lines of activity. The viewer is the IMO's owner.
 */
/**
 * The advisors the All Clients "Create under" picker offers during a
 * walkthrough: the sample downline's people who carry a client book. A support
 * admin building for the team practises on these, never on real colleagues
 * (Drew, 2026-10-01: the tour runs on mock data, full stop).
 */
export function sampleAdvisors(): { id: string; label: string }[] {
  return SAMPLE_DOWNLINE.members
    .filter(m => m.status === 'active' && m.has_client_book !== false)
    .map(m => ({ id: m.user_id, label: m.full_name }));
}

export const SAMPLE_DOWNLINE: HierarchyPayload = {
  root_org_id: 'sample-imo',
  is_platform_view: false,
  viewer_granted: { alts: true, fia: true },
  orgs: [
    { id: 'sample-imo', name: 'Summit Financial Group (sample)', org_type: 'imo', parent_org_id: null, owner_name: 'You' },
    { id: 'sample-agency-dallas', name: 'Dallas Agency', org_type: 'agency', parent_org_id: 'sample-imo', owner_name: 'Priya Raman' },
    { id: 'sample-agency-austin', name: 'Austin Agency', org_type: 'agency', parent_org_id: 'sample-imo', owner_name: 'Marcus Bell' },
    { id: 'sample-team-rivera', name: 'Team Rivera', org_type: 'team', parent_org_id: 'sample-agency-dallas', owner_name: 'Luis Rivera' },
  ],
  members: [
    { user_id: 'sample-u-priya', org_id: 'sample-agency-dallas', full_name: 'Priya Raman', email: 'priya@example.com', status: 'active', org_role: 'org_owner', client_count: 2, has_client_book: true },
    { user_id: 'sample-u-ray', org_id: 'sample-agency-dallas', full_name: 'Ray Okafor', email: 'ray@example.com', status: 'active', org_role: 'org_admin', client_count: 0, has_client_book: false },
    { user_id: 'sample-u-dana', org_id: 'sample-agency-dallas', full_name: 'Dana Whitfield', email: 'dana@example.com', status: 'active', org_role: 'agent', client_count: 3, has_client_book: true },
    { user_id: 'sample-u-omar', org_id: 'sample-agency-dallas', full_name: 'Omar Haddad', email: 'omar@example.com', status: 'active', org_role: 'agent', client_count: 1, has_client_book: true },
    { user_id: 'sample-u-luis', org_id: 'sample-team-rivera', full_name: 'Luis Rivera', email: 'luis@example.com', status: 'active', org_role: 'org_owner', client_count: 1, has_client_book: true },
    { user_id: 'sample-u-kim', org_id: 'sample-team-rivera', full_name: 'Kim Sato', email: 'kim@example.com', status: 'active', org_role: 'agent', client_count: 2, has_client_book: true },
    { user_id: 'sample-u-marcus', org_id: 'sample-agency-austin', full_name: 'Marcus Bell', email: 'marcus@example.com', status: 'active', org_role: 'org_owner', client_count: 0, has_client_book: true },
    { user_id: 'sample-u-tessa', org_id: 'sample-agency-austin', full_name: 'Tessa Nguyen', email: 'tessa@example.com', status: 'active', org_role: 'agent', client_count: 0, has_client_book: true },
    { user_id: 'sample-u-chris', org_id: 'sample-agency-austin', full_name: 'Chris Lane', email: 'chris@example.com', status: 'suspended', org_role: 'agent', client_count: 1, has_client_book: true },
  ],
  clients: [
    { client_id: 'sample-c-1', agent_user_id: 'sample-u-priya', name: 'Harold & June Peterson', updated_at: daysAgo(2) },
    { client_id: 'sample-c-2', agent_user_id: 'sample-u-priya', name: 'Delgado Family Trust', updated_at: daysAgo(9) },
    { client_id: 'sample-c-3', agent_user_id: 'sample-u-dana', name: 'Robert Chen', updated_at: daysAgo(1) },
    { client_id: 'sample-c-4', agent_user_id: 'sample-u-dana', name: 'Ana & Felix Moreau', updated_at: daysAgo(4) },
    { client_id: 'sample-c-5', agent_user_id: 'sample-u-dana', name: 'Whitaker Holdings', updated_at: daysAgo(12) },
    { client_id: 'sample-c-6', agent_user_id: 'sample-u-omar', name: 'Grace Okonkwo', updated_at: daysAgo(3) },
    { client_id: 'sample-c-7', agent_user_id: 'sample-u-luis', name: 'The Brennan Family', updated_at: daysAgo(6) },
    { client_id: 'sample-c-8', agent_user_id: 'sample-u-kim', name: 'Samuel Park', updated_at: daysAgo(1) },
    { client_id: 'sample-c-9', agent_user_id: 'sample-u-kim', name: 'Elena Vasquez', updated_at: daysAgo(15) },
    { client_id: 'sample-c-10', agent_user_id: 'sample-u-chris', name: 'Mitchell & Sons', updated_at: daysAgo(40) },
  ],
  flags: [{ org_id: 'sample-agency-austin', flag_name: 'alts', enabled: false }],
  user_flags: [],
  audit: [
    { actor_name: 'You', action: 'invite.created', target_type: 'org_invite', details: { email: 'tessa@example.com', kind: 'agent' }, created_at: daysAgo(1) },
    { actor_name: 'Priya Raman', action: 'scenario.created', target_type: 'scenario', details: { scenario_name: 'Harold & June Peterson (Roth)' }, created_at: daysAgo(2) },
    { actor_name: 'You', action: 'org.flag_changed', target_type: 'organization', details: { flag: 'alts', enabled: false, org_name: 'Austin Agency' }, created_at: daysAgo(5) },
    { actor_name: 'You', action: 'user.deactivated', target_type: 'profile', details: { name: 'Jordan Reyes' }, created_at: daysAgo(20) },
  ],
};

/** One line for every screen that shows sample data. */
export const SAMPLE_NOTICE = 'Sample data for the walkthrough. Nothing here is real, and it is removed when the walkthrough ends.';
